import React, { useContext, useState } from 'react';
import dayjs from 'dayjs';
import { FaSpinner } from 'react-icons/fa';

import { SessionContext } from '@benzinga/session-context';
import { UserSubscription } from '@benzinga/subscription-manager';
import { Card, IPrecheckoutResponse, ShopManager } from '@benzinga/shop-manager';
import { PlusProduct } from '@benzinga/plus-manager';
import { LeftArrow } from '@benzinga/themed-icons';
import { OrderSummary, CreditCardBlock, OptionButton, isProToPro } from './components';

interface ChangePlanFlowProps {
  setShowChangePlan: React.Dispatch<React.SetStateAction<boolean>>;
  setShowInitial: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  subscription: UserSubscription;
  alternativePlans: PlusProduct[];
  creditCards: Card[];
  handleSubscriptionCheckout: (IPrecheckoutResponse, coupon, oldSub, paymentCard) => Promise<boolean>;
  handleSubscriptionUpgrade: (IPrecheckoutResponse, coupon, paymentCard) => Promise<boolean>;
}

export const ChangePlanFlow: React.FC<ChangePlanFlowProps> = ({
  alternativePlans,
  creditCards,
  handleClose,
  handleSubscriptionCheckout,
  handleSubscriptionUpgrade,
  setShowChangePlan,
  setShowInitial,
  subscription,
}) => {
  const [showOptions, setShowOptions] = useState(true);
  const [showSubOptions, setShowSubOptions] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [selectedPackage, setSelectedPackage] = useState<IPrecheckoutResponse | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<PlusProduct | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(creditCards[0] ?? null);
  const [productPrecheckouts, setProductPrecheckouts] = useState<IPrecheckoutResponse[]>([]);

  const [switchToMonthly, setSwitchToMonthly] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const session = useContext(SessionContext);

  const handleChangePlanSubmit = async () => {
    setShowError(false);
    setIsLoading(true);

    const result =
      switchToMonthly || isProToPro(subscription, selectedPackage)
        ? await handleSubscriptionUpgrade(selectedPackage, '', selectedCard)
        : await handleSubscriptionCheckout(selectedPackage, '', subscription, selectedCard);

    if (result) {
      setShowConfirm(true);
      setShowReview(false);
    } else {
      setShowError(true);
    }
    setIsLoading(false);
  };

  const handleReviewDetailsSubmit = async () => {
    setShowReview(true);
    setShowSubOptions(false);
  };

  const handleContinue = async () => {
    const precheckouts = selectedProduct?.purchaseSlugs?.map(async plan => {
      const checkout = await session.getManager(ShopManager).preCheckout({
        selected_plan: plan,
        subscription: '',
      });
      return checkout.ok;
    });
    let precheckoutsResolved = precheckouts && precheckouts?.length > 0 ? await Promise.all(precheckouts) : [];
    precheckoutsResolved = precheckoutsResolved.filter(checkout => checkout !== undefined);

    setProductPrecheckouts(precheckoutsResolved as IPrecheckoutResponse[]);

    if (precheckoutsResolved.length === 1) {
      setSelectedPackage(precheckoutsResolved[0] as IPrecheckoutResponse);
      if (selectedProduct?.change === 'monthly') setSwitchToMonthly(true);
      setShowOptions(false);
      setShowReview(true);
    } else {
      setShowSubOptions(true);
      setShowOptions(false);
    }
  };

  const handleGoBack = () => {
    if (showReview) {
      setShowReview(false);
      setShowError(false);
      if (selectedProduct?.purchaseSlugs?.length === 1) {
        setShowOptions(true);
      } else {
        setShowSubOptions(true);
      }
    } else if (showSubOptions) {
      setShowSubOptions(false);
      setShowOptions(true);
      setSelectedPackage(null);
    } else {
      setShowChangePlan(false);
      setShowInitial(true);
    }
  };

  return (
    <div className="flex flex-col gap-2 h-full overflow-y-auto">
      <div className="mb-2">
        <h2>Change Plan</h2>
        <p className="text-xs m-0">Unsatisfied with your plan? Try another</p>
      </div>
      {showOptions && alternativePlans && (
        <>
          <div>Please select the service you would like to try:</div>
          <div className="overflow-y-scroll">
            <div className="grid grid-cols-1 gap-2 text-sm pt-2">
              {alternativePlans.map((product, idx) => {
                return (
                  <OptionButton
                    index={idx}
                    key={product.slug}
                    label={product.title}
                    selectedVal={selectedProduct}
                    setMethod={setSelectedProduct}
                    value={product}
                  />
                );
              })}
            </div>
          </div>
          <button
            className={`px-4 py-2 ${selectedProduct ? 'bg-bzorange-500 text-white' : 'bg-bzgrey-300/80 text-bzgrey-400'}`}
            disabled={!selectedProduct}
            onClick={handleContinue}
          >
            Continue
          </button>
        </>
      )}
      {showSubOptions && (
        <div className="flex flex-col justify-between h-full">
          <div>
            <div className="flex flex-col gap-2">
              <div>Please select which plan to change with:</div>
              {productPrecheckouts?.length > 0 &&
                productPrecheckouts.map((plan, idx) => {
                  return (
                    <OptionButton
                      index={idx}
                      key={plan.plan.name}
                      label={plan.plan.name}
                      selectedVal={selectedPackage}
                      setMethod={setSelectedPackage}
                      value={plan}
                    />
                  );
                })}
              {productPrecheckouts?.length === 0 && (
                <div className="font-bold mt-4">
                  No plans available for this product. Please check again later or contact our support team.
                </div>
              )}
            </div>
          </div>
          <button
            className={`px-4 py-2 ${
              selectedPackage ? 'bg-bzorange-500 text-white' : 'bg-bzgrey-300/80 text-bzgrey-400'
            }`}
            onClick={handleReviewDetailsSubmit}
          >
            Review Details
          </button>
        </div>
      )}
      {showReview && (
        <div className="flex flex-col gap-2 h-full justify-between">
          <div>
            <OrderSummary checkout={selectedPackage} title="Order Summary" />
            <CreditCardBlock creditCards={creditCards} selectedCard={selectedCard} setSelectedCard={setSelectedCard} />
            <div className="pt-4">
              {switchToMonthly ? (
                <p>
                  Your subscription will be switched to a monthly plan{' '}
                  {subscription.currentPeriodEnd
                    ? 'on ' + dayjs(subscription.currentPeriodEnd).format('MMM DD, YYYY')
                    : 'today'}
                  .
                </p>
              ) : (
                <p>
                  You will no longer have access to {subscription.planName} features after{' '}
                  {subscription.currentPeriodEnd
                    ? 'on ' + dayjs(subscription.currentPeriodEnd).format('MMM DD, YYYY')
                    : 'today'}
                  .
                </p>
              )}
              {showError && (
                <p className="text-red-500">
                  There was an issue completing your order. Please try again later or contact our support team.
                </p>
              )}
            </div>
          </div>
          <button
            className={`px-4 py-2 mx-auto mt-4 rounded-md w-full ${
              selectedCard && selectedPackage ? 'bg-bzblue-700 text-white' : 'bg-bzgrey-300/80 text-bzgrey-400'
            }`}
            disabled={!selectedCard || !selectedPackage || isLoading}
            onClick={handleChangePlanSubmit}
          >
            {isLoading ? (
              <div className="w-full flex justify-center">
                <FaSpinner className="animate-spin mr-2 h-4 w-4 text-white my-1" />
              </div>
            ) : (
              'Complete Plan Changes'
            )}
          </button>
        </div>
      )}
      {showConfirm && (
        <div className="flex flex-col justify-between h-full">
          <div>
            <div className="text-2xl py-2 font-bold">We&apos;re happy to see you stay!</div>
            <OrderSummary checkout={selectedPackage} title="Order Completed" />
            <div>
              Please allow a few minutes for the updates to become live.
              {!switchToMonthly
                ? ` Check your email to get instructions on how to
                start with your new subscription plan.`
                : ''}
            </div>
          </div>
          <button className="px-4 py-2 rounded-md border border-bzgrey-500/80 text-bzgrey-500/80" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
      {/* Back button */}
      {!showConfirm && (
        <button className="absolute top-2 left-2" onClick={handleGoBack}>
          <LeftArrow />
        </button>
      )}
    </div>
  );
};
